import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { CURRENT_TOS_VERSION } from './utils/constants';
import UXOHire from './UXOHire';
import TechDashboard from './TechDashboard';
import EmployerDashboard from './EmployerDashboard';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EmployerOnboarding from './pages/EmployerOnboarding';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AdminDashboard from './pages/AdminDashboard';
import SupportPage from './pages/SupportPage';
import TosReacceptModal from './components/TosReacceptModal';
import RouteMeta from './components/RouteMeta';

function MaintenancePage() {
  return (
    <div style={{
      fontFamily: "'Georgia','Times New Roman',serif",
      background: '#0d0f10', color: '#e8e4dc', minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        background: '#111316', border: '1px solid #1e2022', borderRadius: 12,
        padding: '48px 40px', maxWidth: 500, width: '100%', textAlign: 'center',
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{"\uD83D\uDEE0\uFE0F"}</div>
        <h1 style={{ fontSize: 24, fontWeight: 'bold', margin: '0 0 12px' }}>Under Maintenance</h1>
        <p style={{ color: '#7a7570', fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          UXOHire is temporarily down for maintenance. We'll be back shortly.
        </p>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [accountType, setAccountType] = useState(null);
  const [showTosModal, setShowTosModal] = useState(false);

  // Detect recovery hash on any page and redirect to /reset-password with hash intact
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('type=recovery') && window.location.pathname !== '/reset-password') {
      window.location.href = '/reset-password' + hash;
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Fetch account type when user changes; auto-create if missing; check ToS version
  // Also auto-heal missing profile/balance records to prevent broken states
  useEffect(() => {
    if (!user) { setAccountType(null); setShowTosModal(false); return; }
    (async () => {
      const { data } = await supabase
        .from('user_accounts')
        .select('account_type, tos_version_accepted')
        .eq('user_id', user.id)
        .maybeSingle();

      let acctType;
      if (data) {
        acctType = data.account_type;
        setAccountType(acctType);
        setShowTosModal(data.tos_version_accepted !== CURRENT_TOS_VERSION);
      } else {
        // No user_accounts record — auto-create from metadata
        const metaType = user.user_metadata?.account_type || 'technician';
        acctType = metaType;
        await supabase.from('user_accounts')
          .upsert({ user_id: user.id, account_type: acctType }, { onConflict: 'user_id', ignoreDuplicates: true });
        setAccountType(acctType);
        setShowTosModal(true);
      }

      // Auto-heal missing records based on account type
      // Skip redirects if already on the target page to prevent loops
      const path = window.location.pathname;

      // The admin dashboard is outside both account-type flows. Admins now hold
      // an ordinary Supabase session, and an admin account has no tech_profiles
      // or employer_profiles row of its own -- so without this the heal below
      // would fire window.location.href and throw them out of /admin, back to
      // /create-profile, in the same moment they finished signing in.
      if (path.startsWith('/admin')) return;

      if (acctType === 'employer') {
        const { data: ep } = await supabase
          .from('employer_profiles').select('id').eq('user_id', user.id).maybeSingle();
        if (!ep) {
          if (path !== '/employer-onboarding') {
            window.location.href = '/employer-onboarding';
          }
          return;
        }
        // Ensure unlock_balance exists
        const { data: ub } = await supabase
          .from('unlock_balance').select('id').eq('employer_id', ep.id).maybeSingle();
        if (!ub) {
          await supabase.from('unlock_balance').insert({
            employer_id: ep.id, current_balance: 10, monthly_allowance: 10,
          });
        }
      } else if (acctType === 'technician') {
        const { data: tp } = await supabase
          .from('tech_profiles').select('id').eq('user_id', user.id).maybeSingle();
        if (!tp) {
          if (path !== '/create-profile') {
            window.location.href = '/create-profile';
          }
          return;
        }
      }
    })();
  }, [user]);

  // Wait for auth and account type to resolve before rendering
  if (user === undefined) return null;
  if (user && accountType === null) return null;

  // Maintenance mode gate — admin route always accessible
  const isMaintenanceMode = localStorage.getItem('uxo_maintenance_mode') === 'true';

  // Same reasoning as the heal skip above: an admin session belongs to a person
  // administering the site, not a member accepting terms, and the modal would
  // otherwise sit on top of the dashboard demanding acceptance. Read from
  // window rather than useLocation because BrowserRouter is mounted below this
  // point; /admin never client-navigates, so a render-time read is stable.
  const onAdminRoute = window.location.pathname.startsWith('/admin');

  return (
    <BrowserRouter>
      <RouteMeta />
      {user && showTosModal && !onAdminRoute && (
        <TosReacceptModal user={user} onAccepted={() => setShowTosModal(false)} />
      )}
      <Routes>
        {/* Admin dashboard — always accessible */}
        <Route path="/admin" element={<AdminDashboard />} />

        {/* If maintenance mode is on, show maintenance page for all other routes */}
        {isMaintenanceMode && (
          <Route path="*" element={<MaintenancePage />} />
        )}

        {/* Tech dashboard — protected, tech accounts only */}
        <Route
          path="/dashboard"
          element={
            !user
              ? <Navigate to="/login" replace state={{ returnTo: '/dashboard' }} />
              : accountType === 'employer'
                ? <Navigate to="/employer-dashboard" replace />
                : <TechDashboard user={user} accountType={accountType} />
          }
        />
        {/* Employer hub — protected, employer accounts only */}
        <Route
          path="/employer-dashboard"
          element={
            !user
              ? <Navigate to="/login" replace state={{ returnTo: '/employer-dashboard' }} />
              : accountType !== 'employer'
                ? <Navigate to="/dashboard" replace />
                : <EmployerDashboard user={user} />
          }
        />
        {/* Employer onboarding — protected, employer accounts only */}
        <Route
          path="/employer-onboarding"
          element={
            !user
              ? <Navigate to="/login" replace state={{ returnTo: '/employer-onboarding' }} />
              : accountType !== 'employer'
                ? <Navigate to="/dashboard" replace />
                : <EmployerOnboarding user={user} />
          }
        />
        {/* Password reset — standalone page (handles Supabase email callback) */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Legal pages */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/support" element={<SupportPage />} />
        {/* Everything else handled by UXOHire (includes its own routing) */}
        <Route path="/*" element={<UXOHire user={user} />} />
      </Routes>
    </BrowserRouter>
  );
}
